import { createSlice } from '@reduxjs/toolkit';

const createInitialFormData = () => ({
  contract_id: '',
  contract_no: '',
  contract_startdate: '',
  contract_enddate: '',
  contract_description: '',
  divisi_id: '',
  customer_id: '',
  customer_name: '',
  project_id: '',
  project_name: '',
  projecttype_id: '',
  projectsubtype_id: '',
  contract_top: '',
  contract_topadendum: '',
  contract_retensi: '',
  contract_tax: 0,
  contract_nilaikontrak: 0,
  contract_post: 0,
  contract_signed: 0,
  contract_docfile: '',
  contract_kirimfile: '',
  contract_kembalifile: '',
  contract_createdby: '',
  contract_createddate: '',
  contract_modifyby: '',
  contract_modifydate: '',
  rowid: '',
  nilaiTagihan: [],
  adendum: [],
  nilaiAdendum: [],
  log: [],
  tabs: {
    nilaiTagihan: 'Nilai Tagihan',
    adendum: 'Adendum',
    nilaiAdendum: 'Nilai Adendum',
    log: 'Log',
    record: 'Record',
  },
});

const createInstanceState = () => ({
  selectedRow: null,
  formData: createInitialFormData(),
  dataList: [],
  totalData: 0,
  loading: false,
  loadingMessage: '',
  error: null,
  activeTab: 0,
});

const initialState = {
  instances: {
    default: createInstanceState(),
  },
};

const defaultInstanceState = createInstanceState();

const readScopedPayload = (payload, valueKey) => {
  if (payload && typeof payload === 'object' && Object.prototype.hasOwnProperty.call(payload, 'instanceKey')) {
    return {
      instanceKey: String(payload.instanceKey || 'default'),
      value: Object.prototype.hasOwnProperty.call(payload, valueKey) ? payload[valueKey] : undefined,
    };
  }
  return {
    instanceKey: 'default',
    value: payload,
  };
};

const ensureInstancesRoot = (state) => {
  if (state.instances && typeof state.instances === 'object') return;
  const hasLegacyRoot = Object.prototype.hasOwnProperty.call(state, 'formData')
    || Object.prototype.hasOwnProperty.call(state, 'dataList')
    || Object.prototype.hasOwnProperty.call(state, 'activeTab');
  if (hasLegacyRoot) {
    state.instances = {
      default: {
        selectedRow: state.selectedRow ?? null,
        formData: { ...createInitialFormData(), ...(state.formData || {}) },
        dataList: Array.isArray(state.dataList) ? state.dataList : [],
        totalData: Number(state.totalData || 0),
        loading: Boolean(state.loading),
        loadingMessage: String(state.loadingMessage || ''),
        error: state.error || null,
        activeTab: Number(state.activeTab || 0),
      },
    };
    return;
  }
  state.instances = { default: createInstanceState() };
};

const ensureInstanceState = (state, instanceKey) => {
  ensureInstancesRoot(state);
  if (!state.instances[instanceKey]) {
    state.instances[instanceKey] = createInstanceState();
  }
  return state.instances[instanceKey];
};

const resetInstanceState = (instanceState) => {
  instanceState.selectedRow = null;
  instanceState.formData = createInitialFormData();
  instanceState.dataList = [];
  instanceState.totalData = 0;
  instanceState.loading = false;
  instanceState.loadingMessage = '';
  instanceState.error = null;
  instanceState.activeTab = 0;
};

const mkt07contractSlice = createSlice({
  name: 'mkt07contract',
  initialState,
  reducers: {
    setSelectedRow: (state, action) => {
      const { instanceKey, value } = readScopedPayload(action.payload, 'selectedRow');
      const instanceState = ensureInstanceState(state, instanceKey);
      instanceState.selectedRow = value;
    },
    setActiveTab: (state, action) => {
      const { instanceKey, value } = readScopedPayload(action.payload, 'activeTab');
      const instanceState = ensureInstanceState(state, instanceKey);
      instanceState.activeTab = Number(value || 0);
    },
    setDataList: (state, action) => {
      const { instanceKey, value } = readScopedPayload(action.payload, 'dataList');
      const instanceState = ensureInstanceState(state, instanceKey);
      instanceState.dataList = Array.isArray(value) ? value : [];
    },
    setTotalData: (state, action) => {
      const { instanceKey, value } = readScopedPayload(action.payload, 'totalData');
      const instanceState = ensureInstanceState(state, instanceKey);
      instanceState.totalData = Number(value || 0);
    },
    setLoading: (state, action) => {
      const { instanceKey, value } = readScopedPayload(action.payload, 'loading');
      const instanceState = ensureInstanceState(state, instanceKey);
      instanceState.loading = Boolean(value);
    },
    setLoadingMessage: (state, action) => {
      const { instanceKey, value } = readScopedPayload(action.payload, 'loadingMessage');
      const instanceState = ensureInstanceState(state, instanceKey);
      instanceState.loadingMessage = String(value || '');
    },
    setErrorMsg: (state, action) => {
      const { instanceKey, value } = readScopedPayload(action.payload, 'error');
      const instanceState = ensureInstanceState(state, instanceKey);
      instanceState.error = value || null;
    },
    updateFormData: (state, action) => {
      const { instanceKey, value } = readScopedPayload(action.payload, 'formData');
      const instanceState = ensureInstanceState(state, instanceKey);
      const nextFormData = (value && typeof value === 'object') ? value : {};
      instanceState.formData = { ...instanceState.formData, ...nextFormData };
    },
    clearFormData: (state, action) => {
      const { instanceKey } = readScopedPayload(action.payload, 'instanceKey');
      const instanceState = ensureInstanceState(state, instanceKey);
      instanceState.formData = createInitialFormData();
    },
    fetchDetailSuccess: (state, action) => {
      const { instanceKey, value } = readScopedPayload(action.payload, 'formData');
      const instanceState = ensureInstanceState(state, instanceKey);
      const nextFormData = (value && typeof value === 'object') ? value : {};
      instanceState.formData = { ...createInitialFormData(), ...nextFormData };
      instanceState.loading = false;
      instanceState.error = null;
    },
    fetchDetailFailure: (state, action) => {
      const { instanceKey, value } = readScopedPayload(action.payload, 'error');
      const instanceState = ensureInstanceState(state, instanceKey);
      instanceState.loading = false;
      instanceState.error = value || null;
    },
    fetchDetailError: (state, action) => {
      const { instanceKey, value } = readScopedPayload(action.payload, 'error');
      const instanceState = ensureInstanceState(state, instanceKey);
      instanceState.loading = false;
      instanceState.error = value || null;
    },
    clearModuleState: (state, action) => {
      const { instanceKey } = readScopedPayload(action.payload, 'instanceKey');
      const instanceState = ensureInstanceState(state, instanceKey);
      resetInstanceState(instanceState);
    },
  },
});

export const selectMkt07ContractInstanceState = (state, instanceKey = 'default') => (
  state?.mkt07contract?.instances?.[instanceKey] || defaultInstanceState
);

export const {
  setSelectedRow,
  setActiveTab,
  setDataList,
  setTotalData,
  setLoading,
  setLoadingMessage,
  setErrorMsg,
  updateFormData,
  clearFormData,
  fetchDetailSuccess,
  fetchDetailFailure,
  fetchDetailError,
  clearModuleState,
} = mkt07contractSlice.actions;

export { createInitialFormData as initialMkt07ContractFormDataFactory };
export default mkt07contractSlice.reducer;
